import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { makeStyles, shorthands, Spinner } from "@fluentui/react-components";
import {
    contactNoteService,
    type ContactNoteInput,
} from "../../services/contactNoteService";
import { useAuth } from "../../context/AuthContext";
import { useUserLabels } from "../../hooks/useUserLabels";
import { NotizEditorDialog } from "./NotizEditorDialog";
import { BlockZuordnenDialog } from "./BlockZuordnenDialog";
import { NotizFeld } from "../notizbloecke/NotizFeld";
import type { BlockAktion } from "../notizbloecke/BlockKontextmenue";
import { bildMimeAusName, KATEGORIE_NOTIZBILD } from "../notizbloecke/bildDarstellung";
import type { BezugKandidat } from "../notizbloecke/bezugKandidaten";
import type { Block } from "../notizbloecke/blockModell";
import { ladeBezugQuellen } from "./bezugQuellen";
import { useNavigate } from "react-router";
import { useOpenDocument } from "../documents/useDocumentViewer";
import { documentService, type DocumentDto } from "../../services/documentService";
import { mailLinkService } from "../../services/mailLinkService";
import { openLinkedMailWindow } from "../mail/LinkedMailWindow";
import type { NoteRefArt } from "../../services/vorgangService";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openVorgangCreateFlow } from "../vorgang/create/VorgangCreateFlowWindow";
import { buildVorgangPrefillFromNote } from "./kundentagebuchLogic";
import {
    ladeKundenNotizen,
    nurEigene,
    sichtbareNotizen,
    type KundenNotizDto,
} from "./kundenNotizen";
import { notizEintraege } from "../kontaktArchiv/notizEintraege";
import { useKundentagebuchLive } from "./useKundentagebuchLive";
import { bestaetigen } from "../redesign/bestaetigen";
import { bestaetigeNotizenLoeschen } from "../notizbloecke/notizenLoeschenFrage";

/**
 * „Dauernotiz" — die Notizfläche ganz oben im Kundentagebuch (PO 08.09.2026).
 *
 * Seit dem 28.09.2026 DASSELBE fortlaufende Notizfeld wie im Vorgang
 * (`NotizFeld`; PO: „Kundentagebuch ist die Notiz fortlaufend und genau wie
 * bei Vorgänge mit demselben Umfang"): ein Textfeld, neueste Notiz oben, das
 * Kürzel am Rand, Formatierung und Bilder, Speichern in der Tipp-Pause und
 * spätestens nach vier Sekunden, fremde Änderungen live. Vorher stand hier der
 * Zettelstapel `NotizFlaeche` mit Kasten je Notiz und Verschiebe-Griffen.
 *
 * ══ NUR DIE EIGENEN NOTIZEN (PO 14.09.2026) ════════════════════════════════
 * „kundentagebuch da nur neue notizen, notizen zu vorgang und todos im archiv
 * anzeigen". Die Fläche zeigt deshalb nur, was im Tagebuch selbst geschrieben
 * wurde ({@link nurEigene}) — inklusive dessen aus dem Umfeld (Ansprechpartner,
 * Verbund, Haushalt). Was aus einem Vorgang oder einem To-do stammt, steht im
 * „Verlauf / Archiv" desselben Reiters, dort nur lesbar: eine Vorgangs-Notiz
 * gehört ihrem Vorgang, und ein zweiter Bearbeitungsort für denselben Text wäre
 * eine Einladung zum Auseinanderlaufen.
 *
 * Ist die Fläche danach LEER — auf Bestandskunden der Normalfall, dort hängt
 * fast jede Notiz an einem Vorgang —, sagt sie es und nennt den Weg: ein
 * ruhiger Satz mit der Zahl der fremden Notizen und einem Verweis, der das
 * Archiv aufklappt (`onArchivOeffnen`). Ohne ihn sähe die Trennung wie ein
 * Datenverlust aus.
 *
 * Die Riegel gegen das Schreiben an fremden Blöcken (`bearbeitbar`, die Prüfung
 * `source !== "kontakt"` in jedem Schreibpfad) bleiben trotzdem stehen: sie
 * kosten nichts und fangen ab, was eine künftige Änderung wieder hereinspülen
 * könnte — die Ids der Vorgangs-Notizen gehören einer anderen Tabelle.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die seltenen Aktionen an einer Notiz — zuordnen, Zuordnung lösen, daraus
 * einen Vorgang machen, löschen — hängen am RECHTSKLICK (PO 08.09.2026), im
 * Notizfeld unter der Formatierleiste (`aktionenFuer`).
 *
 * Echtzeit: {@link useKundentagebuchLive} lädt bei fremden Änderungen neu und
 * zeigt den Entwurfstext, den gerade jemand anderes im Editor tippt.
 *
 * `#` öffnet die Bezugsauswahl. Ihr Angebot (Mails, Anrufe, Dokumente,
 * Verträge, Risiken) wird ERST beim ersten Hineinklicken nachgeladen — siehe
 * {@link ladeBezugQuellen}.
 */
export interface KundentagebuchNotizenHandle {
    openNewNote: () => void;
}

interface Props {
    contactId: number;
    /** Meldet die Gesamtzahl nach oben (Zähler im Tab-Header). */
    onCountChange?: (count: number) => void;
    /**
     * Meldet nach oben, wie viele Notizen aus Vorgängen und To-dos im Strom
     * liegen — also die, die seit der Trennung im „Verlauf / Archiv" stehen.
     *
     * Der Grund ist ein doppelter: der leere Zustand hier nennt die Zahl, und
     * der Kopf der Archiv-Karte kann sie ZUGEKLAPPT zeigen, ohne dafür zu
     * laden. Sie kostet nichts — die Liste, die diese Fläche ohnehin holt,
     * enthält die fremden Notizen bereits; das Archiv lädt sie später nur
     * deshalb selbst, weil es auch ohne diese Fläche laufen können muss.
     */
    onFremdeCountChange?: (count: number) => void;
    /**
     * Klick auf den Verweis „Verlauf / Archiv" im leeren Zustand: der Aufrufer
     * klappt den Archiv-Abschnitt auf. Fehlt er, bleibt der Verweis Text —
     * lieber keine Sprungmarke als eine, die nichts tut.
     */
    onArchivOeffnen?: () => void;
    /**
     * Ohne eigene Kopfzeile („Dauernotiz" + Zähler + „+ Neue Notiz") und ohne
     * Karten-Rahmen rendern — dann liefert die umgebende Klapp-Sektion Titel,
     * Zähler und Aktion. Sonst stünden Titel und „+ Neue Notiz" doppelt da.
     */
    headerless?: boolean;
}

const useStyles = makeStyles({
    section: {
        ...shorthands.margin("10px", "12px"),
        ...shorthands.border("1px", "solid", "#e3e0e7"),
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        ...shorthands.padding("8px", "16px"),
        backgroundColor: "#f7f5fa",
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        columnGap: "8px",
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "#6f6c75",
        fontWeight: 600,
    },
    countBadge: {
        display: "inline-block",
        ...shorthands.padding("1px", "7px"),
        fontSize: "10px",
        backgroundColor: "#e3e0e7",
        borderRadius: "999px",
        color: "#2d142b",
        letterSpacing: "0",
    },
    addBtn: {
        ...shorthands.padding("5px", "12px"),
        borderRadius: "999px",
        ...shorthands.border("1px", "solid", "#4d2051"),
        backgroundColor: "#4d2051",
        color: "#ffffff",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "11px",
        fontWeight: 700,
        ":hover": { backgroundColor: "#3a163d" },
    },
    error: {
        ...shorthands.padding("8px", "16px"),
        fontSize: "12px",
        color: "#b91c1c",
    },
    // Der leere Zustand: ein ruhiger Satz über der Schreibzeile, kein Kasten
    // und kein Ausrufezeichen — hier ist nichts kaputt, hier ist nur noch
    // nichts geschrieben.
    leer: {
        ...shorthands.padding("10px", "16px", "0"),
        fontSize: "13px",
        lineHeight: "1.5",
        color: "#6f6c75",
    },
    leerLink: {
        ...shorthands.padding("0"),
        ...shorthands.border("0"),
        backgroundColor: "transparent",
        color: "#4d2051",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: "13px",
        fontWeight: 600,
        textDecorationLine: "underline",
        ":hover": { color: "#3a163d" },
    },
    draft: {
        display: "flex",
        flexDirection: "column",
        rowGap: "3px",
        ...shorthands.padding("10px", "16px"),
        ...shorthands.borderTop("1px", "solid", "#f0edf2"),
        backgroundColor: "#fdfcfd",
    },
    draftHead: {
        fontSize: "11px",
        color: "#8a5a00",
        fontWeight: 600,
    },
    draftText: {
        fontSize: "13px",
        color: "#9a8ba0",
        fontStyle: "italic",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        borderLeftWidth: "2px",
        borderLeftStyle: "solid",
        borderLeftColor: "#f0dca0",
        paddingLeft: "8px",
    },
});

export const KundentagebuchNotizen = forwardRef<KundentagebuchNotizenHandle, Props>(
    (
        {
            contactId,
            onCountChange,
            onFremdeCountChange,
            onArchivOeffnen,
            headerless = false,
        },
        ref,
    ) => {
        const styles = useStyles();
        const { user } = useAuth();
        const currentUserId: string | null = user?.id ?? null;
        const { users, label } = useUserLabels();
        const { openWindow } = useWindowManager();

        const [notizen, setNotizen] = useState<KundenNotizDto[]>([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState<string | null>(null);
        const [editorOpen, setEditorOpen] = useState(false);

        const [zuordnenFuer, setZuordnenFuer] = useState<Block | null>(null);

        // Angebot hinter dem `#`. Die Kommunikation wird faul geladen, die
        // Personen kommen aus `useUserLabels` und stehen sofort zur Verfügung.
        const [kommKandidaten, setKommKandidaten] = useState<BezugKandidat[]>([]);
        const quellenAngefordert = useRef(false);

        /**
         * Überholte Antworten zählen nicht: Autosave, Live-Signal und Polling
         * laden gleichzeitig — kam eine ältere Antwort zuletzt an, stünde im
         * Feld kurz wieder der alte Text.
         */
        const ladeLaufRef = useRef(0);
        const load = useCallback(async () => {
            setError(null);
            const lauf = ++ladeLaufRef.current;
            try {
                const liste = await ladeKundenNotizen(contactId);
                if (lauf === ladeLaufRef.current) setNotizen(liste);
            } catch (err: any) {
                setError(err?.message ?? "Notizen konnten nicht geladen werden");
            } finally {
                setLoading(false);
            }
        }, [contactId]);

        useEffect(() => {
            setLoading(true);
            void load();
        }, [load]);

        // Beim Kontaktwechsel gilt das alte Angebot nicht mehr — sonst böte die
        // Auswahl die Mails des zuvor geöffneten Kunden an.
        useEffect(() => {
            quellenAngefordert.current = false;
            setKommKandidaten([]);
        }, [contactId]);

        /**
         * Beim ersten Hineinklicken nachladen, danach nie wieder. Ein
         * Fehlschlag wird in {@link ladeBezugQuellen} je Quelle abgefangen; die
         * Auswahl ist dann schmaler, das Schreiben stört das nicht.
         */
        const quellenSicherstellen = useCallback(() => {
            if (quellenAngefordert.current) return;
            quellenAngefordert.current = true;
            void ladeBezugQuellen(contactId).then(setKommKandidaten);
        }, [contactId]);

        /**
         * Hinter `#` stehen nur Bezüge (Mails, Anrufe, Dokumente, Verträge,
         * Risiken) — KEINE Personen, wie im Vorgang seit #1834 (PO 28.09.2026:
         * „Personen bei # raus"). Personen erwähnt man mit `@`.
         */
        const kandidaten = kommKandidaten;

        const navigate = useNavigate();
        const openDocument = useOpenDocument();

        /**
         * Klick auf einen Bezug in der Notiz.
         *
         * Risiko und Vertrag brauchen nur ihre Id. Mail und Dokument brauchen
         * das ganze Objekt — und anders als im Vorgang liegt das hier nicht
         * schon geladen vor (das Tagebuch holt seine Blöcke über einen eigenen
         * Endpunkt und kennt die Kommunikation nicht). Deshalb wird je Klick
         * gezielt nachgeschlagen; ein Fehlschlag bleibt in der Konsole, statt
         * dem Schreibenden einen Dialog vor die Nase zu setzen.
         */
        const bezugOeffnen = useCallback(
            (art: NoteRefArt, id: string) => {
                if (art === "risiko") {
                    navigate(`/risks/${id}`);
                    return;
                }
                if (art === "vertrag") {
                    navigate(`/contracts/${id}`);
                    return;
                }
                if (art === "dokument") {
                    void documentService
                        .fetchOne(Number(id))
                        .then((d: DocumentDto) =>
                            openDocument(d, {
                                download: (docId) => void documentService.openSignedDownload(docId),
                            }),
                        )
                        .catch((err: unknown) =>
                            console.error("[Kundentagebuch] Dokument öffnen fehlgeschlagen", err),
                        );
                    return;
                }
                if (art !== "mail") return;
                void mailLinkService
                    .listByContact(contactId)
                    .then((links) => {
                        const link = links.find((l) => String(l.id) === id);
                        if (!link) return;
                        openLinkedMailWindow(openWindow, link);
                    })
                    .catch((err: unknown) =>
                        console.error("[Kundentagebuch] Mail öffnen fehlgeschlagen", err),
                    );
            },
            [contactId, navigate, openDocument, openWindow],
        );

        // Echtzeit: fremde Notizen einspiegeln + Entwurfstext anderer anzeigen.
        const live = useKundentagebuchLive(contactId, { onNotesChanged: load });
        const { fremdeEntwuerfe, sendeEntwurf } = live;
        // Unbekannt heißt: nicht pollen.
        const liveVerbunden = live.connected ?? true;

        // Ohne Socket trotzdem live: alle 10 s nachfragen, solange die Seite
        // sichtbar ist. Das Notizfeld trägt nur ein, was hier nicht
        // ungespeichert steht.
        useEffect(() => {
            if (liveVerbunden) return;
            const t = window.setInterval(() => {
                if (document.visibilityState === "visible") void load();
            }, 10_000);
            return () => window.clearInterval(t);
        }, [liveVerbunden, load]);

        /**
         * Die Notizen dieser Fläche: die EIGENEN (siehe Kopfkommentar). Alles
         * Weitere hier unten rechnet auf dieser Liste — Zähler, Blöcke,
         * Rechtsklick-Menü —, damit Kopf und Fläche nicht auseinanderfallen
         * können.
         */
        const eigene = useMemo(() => nurEigene(notizen), [notizen]);

        /**
         * Herkunft und Beschriftung hängen an der DTO, nicht am Block — das
         * Blockmodell soll nichts über Vorgänge und Kontakte wissen. Schlüssel
         * ist derselbe Composite-Key, den `bloeckeAus` vergibt.
         */
        const dtoNach = useMemo(
            () => new Map(eigene.map((n) => [`${n.source}:${n.id}`, n])),
            [eigene],
        );


        /**
         * Das Tagebuch ist EIN Strang: die Fläche soll auch zeigen, was an
         * einem To-do hängt. `todoId` wird deshalb geleert, sonst filterte
         * `bloeckeAus` genau diese Notizen heraus. Verloren geht dabei nichts —
         * das To-do steht im Herkunfts-Badge.
         */
        const flaechenNotizen = useMemo(
            () => eigene.map((n) => ({ ...n, todoId: null })),
            [eigene],
        );

        /**
         * Gezählt wird, was auch zu SEHEN ist — dieselbe Liste, dieselbe Regel:
         * nur eigene Notizen (`eigene`), und davon keine inhaltsleeren, denn die
         * lässt die Fläche weg (`bloeckeAus`). Zählte der Kopf weiter alle,
         * stünde dort „5" über zwei Blöcken — die Diskrepanz, die abgeschafft
         * werden sollte.
         */
        const anzahl = useMemo(() => sichtbareNotizen(eigene).length, [eigene]);

        /**
         * Wie viele Notizen aus Vorgängen und To-dos im Strom liegen — die
         * also, die seit der Trennung im „Verlauf / Archiv" stehen.
         *
         * Gezählt wird mit {@link notizEintraege}, der GENAU DERSELBEN
         * Ableitung, aus der das Archiv seine Notiz-Zeilen baut. Eine zweite,
         * hier nachgebaute Zählregel liefe über kurz oder lang auseinander —
         * und dann behauptete der zugeklappte Kopf eine Zahl, die man nach dem
         * Aufklappen nicht wiederfindet. Der Aufruf ist reine Rechnerei
         * (Reguläre Ausdrücke, kein DOM, kein Abruf) auf einer Liste, die
         * ohnehin schon geladen ist.
         */
        const fremdeAnzahl = useMemo(() => notizEintraege(notizen).length, [notizen]);

        // Erst nach dem Laden melden — sonst blinkt im Sektionskopf beim Öffnen
        // kurz eine 0 auf, bevor die echte Anzahl eintrifft.
        useEffect(() => {
            if (!loading) onCountChange?.(anzahl);
        }, [loading, anzahl, onCountChange]);

        useEffect(() => {
            if (!loading) onFremdeCountChange?.(fremdeAnzahl);
        }, [loading, fremdeAnzahl, onFremdeCountChange]);

        useImperativeHandle(
            ref,
            () => ({
                openNewNote: () => setEditorOpen(true),
            }),
            [],
        );

        /**
         * Wie im Vorgang: ein Notizbild ist ein Dokument (Kategorie
         * „Notizbild", aus allen Dokumentlisten herausgehalten) — hier am
         * Kontakt. Für das Feld und den Dialog „Neue Notiz".
         */
        const bildHochladen = async (file: File) => {
            const doc = await documentService.upload({
                file,
                contactId,
                category: KATEGORIE_NOTIZBILD,
            });
            return { id: doc.id };
        };

        /** Der Editor-Dialog bleibt für die volle Notiz (Zuweisung, Partner). */
        const handleSubmit = async (input: ContactNoteInput) => {
            await contactNoteService.create(contactId, input);
            sendeEntwurf("");
            await load();
        };

        const canEdit = currentUserId != null;

        /** Notiz löschen (Soft-Delete). Kommentare gehen serverseitig mit. */
        const loeschen = async (block: Block) => {
            if (
                !(await bestaetigen({
                    titel: "Notiz löschen?",
                    text: "Die Notiz und ihre Kommentare werden gelöscht.",
                    ja: "Löschen",
                    ton: "danger",
                }))
            )
                return;
            try {
                await contactNoteService.remove(block.id);
                await load();
            } catch (err: any) {
                setError(err?.message ?? "Notiz konnte nicht gelöscht werden");
            }
        };

        /** Bezug wieder lösen — dieselbe Route, nur ohne Ziel. */
        const zuordnungLoesen = async (block: Block) => {
            try {
                await contactNoteService.zuordnen(block.id, { vorgangId: null, todoId: null });
                await load();
            } catch (err: any) {
                setError(err?.message ?? "Zuordnung konnte nicht gelöst werden");
            }
        };

        /**
         * Notiz → Vorgang: Erstell-Flow mit der Zuweisung der Notiz vorbelegt
         * öffnen; sobald der Vorgang steht, WANDERT die Notiz per
         * `moveToVorgang` dorthin und verschwindet aus dem Tagebuch. Der
         * Kontakt kommt aus der Herkunft — bei einer Notiz aus dem Umfeld
         * (Verbund, Haushalt, Ansprechpartner) ist das nicht der geöffnete
         * Kunde, und der Vorgang gehörte sonst dem Falschen.
         */
        const zuVorgangMachen = (dto: KundenNotizDto) => {
            openVorgangCreateFlow(openWindow, {
                ...buildVorgangPrefillFromNote({
                    contactId: dto.herkunft.kontaktId,
                    contractId: dto.contractId ?? null,
                    riskObjectId: dto.riskObjectId ?? null,
                }),
                onCreated: async (result) => {
                    if (!result.vorgangId) return;
                    try {
                        await contactNoteService.moveToVorgang(dto.id, result.vorgangId);
                    } catch (err: any) {
                        setError(
                            err?.message ??
                                "Vorgang wurde angelegt, die Notiz konnte aber nicht verschoben werden",
                        );
                    } finally {
                        await load();
                    }
                },
            });
        };

        /**
         * Die Punkte am Rechtsklick. Angeboten wird nur, was hier auch
         * durchgeht: alles darunter schreibt über `contactNoteService`, dessen
         * Ids der ContactNote-Tabelle gehören — ein Block aus einem Vorgang
         * träfe dort eine fremde Zeile. Löschen und Umzug gelten seit dem
         * 16.09.2026 für jeden Block dieses Tagebuchs, nicht mehr nur für die
         * eigenen — genau wie das Backend es hält; nur Kommentare bleiben bei
         * ihrem Verfasser.
         *
         * An einem Block aus einer FREMDEN Tabelle bleibt der Rechtsklick
         * unangetastet — dort öffnet das Browser-Menü, mit dem sich der Text
         * kopieren lässt.
         */
        const aktionenFuer = (block: Block): BlockAktion[] => {
            if (block.source !== "kontakt") return [];
            const dto = dtoNach.get(block.key);
            if (!dto) return [];
            const punkte: BlockAktion[] = [
                { label: "Einem Vorgang zuordnen …", onClick: () => setZuordnenFuer(block) },
            ];
            if (dto.herkunft.vorgangId != null) {
                punkte.push({ label: "Zuordnung lösen", onClick: () => void zuordnungLoesen(block) });
            }
            // ══ AM BLOCK ARBEITET JEDER, AM KOMMENTAR NUR SEIN VERFASSER ═══
            // Seit PO 16.09.2026 hängen die Handlungen am BLOCK nicht mehr am
            // Autor — das Backend prüft das Schreibrecht am Kontakt
            // (`ContactNoteService.update`/`.remove`), und `moveToVorgang` war
            // ohnehin nie auf den Autor beschränkt. Ein KOMMENTAR (`parentId`)
            // bleibt beim Verfasser; ihn einzeln umzuziehen lehnt das Backend
            // zusätzlich ab. Ohne diese Angleichung stünde hier „Löschen" nicht
            // im Menü, obwohl das Leerräumen desselben Blocks ihn längst löscht.
            const istBlock = block.parentId == null;
            if (istBlock) {
                punkte.push({
                    label: "Daraus einen Vorgang machen …",
                    trennerDavor: true,
                    onClick: () => zuVorgangMachen(dto),
                });
            }
            if (istBlock || block.autor === currentUserId) {
                punkte.push({
                    label: "Löschen",
                    gefahr: true,
                    trennerDavor: true,
                    onClick: () => void loeschen(block),
                });
            }
            return punkte;
        };

        return (
            <div className={headerless ? undefined : styles.section}>
                {!headerless && (
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            <span>Dauernotiz</span>
                            {anzahl > 0 && (
                                <span className={styles.countBadge}>{anzahl}</span>
                            )}
                        </div>
                        <button
                            type="button"
                            className={styles.addBtn}
                            onClick={() => setEditorOpen(true)}
                        >
                            + Neue Notiz
                        </button>
                    </div>
                )}

                {error && <div className={styles.error}>{error}</div>}

                {/* Was gerade jemand anderes tippt (Echtzeit-Entwurf). */}
                {fremdeEntwuerfe.map((d) => (
                    <div key={d.userId} className={styles.draft}>
                        <div className={styles.draftHead}>✎ {label(d.userId)} schreibt gerade …</div>
                        {d.text && <div className={styles.draftText}>{d.text}</div>}
                    </div>
                ))}

                {/* ══ DER LEERE ZUSTAND SAGT, WO DIE NOTIZEN SIND ═══════════
                    Seit der Trennung (PO 14.09.2026) zeigt diese Fläche nur
                    noch die eigenen Notizen — und auf Bestandskunden gibt es
                    davon oft keine einzige, weil alles Geschriebene an einem
                    Vorgang oder To-do hängt. Ohne diesen Satz stünde dort eine
                    leere Fläche neben einer zugeklappten Karte, und das sieht
                    aus wie ein Datenverlust, obwohl nichts fehlt.

                    Nur bei WIRKLICH leerer Fläche; bei einem Ladefehler
                    schweigt er, sonst behauptete er „noch keine Notiz" über
                    einem Strom, den wir gar nicht kennen. */}
                {!loading && !error && anzahl === 0 && (
                    <div className={styles.leer}>
                        Noch keine eigene Notiz.
                        {fremdeAnzahl > 0 && (
                            <>
                                {" "}
                                {fremdeAnzahl === 1
                                    ? "Eine Notiz aus einem Vorgang oder To-do steht"
                                    : `${fremdeAnzahl} Notizen aus Vorgängen und To-dos stehen`}{" "}
                                im{" "}
                                {onArchivOeffnen ? (
                                    <button
                                        type="button"
                                        className={styles.leerLink}
                                        onClick={onArchivOeffnen}
                                    >
                                        Verlauf / Archiv
                                    </button>
                                ) : (
                                    "Verlauf / Archiv"
                                )}
                                .
                            </>
                        )}
                    </div>
                )}

                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                        <Spinner size="tiny" label="Notizen werden geladen..." />
                    </div>
                ) : (
                    // Der Fokus IRGENDWO in der Fläche ist das Startsignal für
                    // das Nachladen des `#`-Angebots — die Fläche selbst soll
                    // davon nichts wissen müssen.
                    <div onFocusCapture={quellenSicherstellen}>
                        <NotizFeld
                            notizen={flaechenNotizen}
                            todoId={null}
                            users={users}
                            canEdit={canEdit}
                            aktuellerUserId={currentUserId ?? ""}
                            herkunftFuer={(b) => dtoNach.get(b.key)?.herkunftLabel ?? null}
                            aktionenFuer={aktionenFuer}
                            onBezugOeffnen={bezugOeffnen}
                            kandidaten={kandidaten}
                            onNeu={async (html, refs, mentions) => {
                                const angelegt = await contactNoteService.create(contactId, {
                                    text: html,
                                    format: "html",
                                    // Nur mitschicken, wenn ein `#`-Bezug gesetzt
                                    // wurde — das Backend ersetzt die Liste.
                                    ...(refs?.length ? { refs } : {}),
                                    ...(mentions?.length ? { mentions } : {}),
                                });
                                await load();
                                // Der Schlüssel zurück ans Feld: Es übernimmt den
                                // Text an Ort und Stelle, der Cursor bleibt.
                                return { key: `kontakt:${angelegt.id}` };
                            }}
                            onAendern={async (block, html, refs, mentions) => {
                                // Ein fremder Block darf hier unter keinen
                                // Umständen schreiben: die Ids der Vorgangs-
                                // Notizen gehören einer anderen Tabelle.
                                if (block.source !== "kontakt") return;
                                // Der Bestand als Rückfall: das Backend ERSETZT
                                // die Erwähnungsliste, ein Block verlöre sonst
                                // bei jeder Textänderung, wen er erwähnt.
                                const erwaehnt = mentions ?? block.mentions;
                                await contactNoteService.update(block.id, {
                                    text: html,
                                    format: "html",
                                    ...(refs?.length ? { refs } : {}),
                                    ...(erwaehnt.length ? { mentions: erwaehnt } : {}),
                                });
                                await load();
                            }}
                            onLoeschen={async (block) => {
                                // Leergeräumt heißt gelöscht (PO 13.09.2026).
                                if (block.source !== "kontakt") return;
                                await contactNoteService.remove(block.id);
                                await load();
                            }}
                            bestaetigeLoeschen={bestaetigeNotizenLoeschen}
                            onBildOeffnen={({ id, name }) =>
                                openDocument(
                                    { id, fileName: name, mimeType: bildMimeAusName(name) },
                                    { download: (docId) => void documentService.openSignedDownload(docId) },
                                )
                            }
                            onBildHochladen={bildHochladen}
                        />
                    </div>
                )}

                {/* Der Schleier fängt den Klick daneben ab und schließt die
                    Auswahl — sie kommt aus einem Rechtsklick und soll die
                    Fläche darunter nicht verschieben. */}
                {zuordnenFuer ? (
                    <div
                        className="nb-zuordnen-schleier"
                        onClick={() => setZuordnenFuer(null)}
                        role="presentation"
                    >
                        <div onClick={(e) => e.stopPropagation()} role="presentation">
                            <BlockZuordnenDialog
                                contactId={contactId}
                                onWaehlen={async (ziel) => {
                                    await contactNoteService.zuordnen(zuordnenFuer.id, ziel);
                                    await load();
                                }}
                                onSchliessen={() => setZuordnenFuer(null)}
                            />
                        </div>
                    </div>
                ) : null}

                <NotizEditorDialog
                    open={editorOpen}
                    contactId={contactId}
                    editing={null}
                    onClose={() => {
                        setEditorOpen(false);
                        sendeEntwurf("");
                    }}
                    onSubmit={handleSubmit}
                    onDraftChange={sendeEntwurf}
                    {...(canEdit ? { onBildHochladen: bildHochladen } : {})}
                />
            </div>
        );
    },
);

KundentagebuchNotizen.displayName = "KundentagebuchNotizen";
